/** 系统内建角色 code（与 main-api role_templates.yaml 对齐）。 */
export type SystemRoleCode =
  | 'field_staff'
  | 'field_supervisor'
  | 'cs_agent'
  | 'cs_lead'
  | 'operations_manager'
  | 'finance_procurement'
  | 'account_administrator'
  | 'vendor'

export interface HelpDoc {
  /** 唯一 id，必须与 public/help/index.html 卡片的 data-name 完全一致 */
  id: string
  /** 所属功能模块，用于分组；同一 module 可拆多张卡 */
  module: string
  /** 适用角色（多对多）。用户角色 ∩ 此列表非空 → 可见。空数组 [] = 无限制，所有人可见（地板卡） */
  roles: SystemRoleCode[]
  /** 文档路径（{lang} 占位由静态页替换）；soon 卡为 null */
  path: string | null
  status: 'active' | 'soon'
}

export const HELP_DOCS: HelpDoc[] = [
  {
    id: 'getting-started',
    module: 'general',
    status: 'active',
    path: 'getting-started/index.html',
    roles: [],
  },
  {
    id: 'reviews',
    module: 'reviews',
    status: 'active',
    path: 'reviews/{lang}.html',
    roles: ['cs_agent', 'cs_lead', 'operations_manager', 'account_administrator'],
  },
  {
    id: 'inbox',
    module: 'inbox',
    status: 'soon',
    path: null,
    roles: ['cs_agent', 'cs_lead', 'operations_manager', 'account_administrator'],
  },
  {
    id: 'support-cases',
    module: 'support',
    status: 'soon',
    path: null,
    roles: ['cs_agent', 'cs_lead', 'operations_manager', 'account_administrator'],
  },
  {
    id: 'tasks',
    module: 'tasks',
    status: 'soon',
    path: null,
    roles: [
      'field_staff',
      'field_supervisor',
      'cs_agent',
      'cs_lead',
      'operations_manager',
      'account_administrator',
      'vendor',
    ],
  },
  {
    id: 'task-triage',
    module: 'tasks',
    status: 'soon',
    path: null,
    roles: ['field_supervisor', 'operations_manager', 'account_administrator'],
  },
  {
    id: 'dashboard',
    module: 'dashboard',
    status: 'soon',
    path: null,
    roles: ['cs_lead', 'operations_manager', 'account_administrator'],
  },
]

/**
 * 当前用户可见的帮助文档。superuser 看全部；roles:[] 地板卡所有人可见；其余按角色并集。
 * 注意：返回结果也包含 status:'soon' 的卡——静态页据 soon 状态显示徽章并禁用点击，
 * 因此把 soon 卡的 id 带进 URL 是预期行为。
 */
export function getVisibleHelpDocs(
  docs: HelpDoc[],
  userRoleCodes: string[],
  isSuperuser: boolean,
): HelpDoc[] {
  // userRoleCodes 来自后端的开放字符串集（可能含非系统的租户自定义角色 code），故用 string[] 而非 SystemRoleCode[]
  if (isSuperuser) return docs
  const roleSet = new Set(userRoleCodes)
  return docs.filter((doc) => doc.roles.length === 0 || doc.roles.some((code) => roleSet.has(code)))
}

/** 拼出带可见文档 id 的 Help Center URL，供 top-bar 打开静态站。 */
export function buildHelpCenterUrl(userRoleCodes: string[], isSuperuser: boolean): string {
  const ids = getVisibleHelpDocs(HELP_DOCS, userRoleCodes, isSuperuser).map((d) => d.id)
  return `/help/index.html?docs=${encodeURIComponent(ids.join(','))}`
}
